import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback } from "react";
import { Table, TableHeader, TableHeaderCell, TableBody, TableRow, TableCell, Button, Input, Spinner, MessageBar, MessageBarBody, Text, Badge, Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions, Field, Switch, } from "@fluentui/react-components";
import { DeleteRegular, ArrowSyncRegular, AddRegular, EditRegular, DismissRegular } from "@fluentui/react-icons";
import { useAuth } from "../../../context/AuthContext";
import { getAllJoursFeries, createJourFerie, updateJourFerie, deleteJourFerie } from "../../../services/dataService";
const emptyForm = { dateFerie: "", libelle: "", annee: String(new Date().getFullYear()), recurrent: false };
export const JoursFeriesView = () => {
    const { apiToken } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const fetchData = useCallback(async () => {
        if (!apiToken)
            return;
        setLoading(true);
        try {
            const data = await getAllJoursFeries(apiToken);
            setItems(data.sort((a, b) => (a.dateFerie > b.dateFerie ? 1 : -1)));
        }
        catch (err) {
            setError(err instanceof Error ? err.message : "Erreur");
        }
        finally {
            setLoading(false);
        }
    }, [apiToken]);
    useEffect(() => { fetchData(); }, [fetchData]);
    const showMessage = (msg, intent) => {
        if (intent === "success") {
            setSuccess(msg);
            setError(null);
        }
        else {
            setError(msg);
            setSuccess(null);
        }
        setTimeout(() => { setSuccess(null); setError(null); }, 3000);
    };
    const openCreate = () => {
        setEditingItem(null);
        setForm(emptyForm);
        setDialogOpen(true);
    };
    const openEdit = (item) => {
        setEditingItem(item);
        setForm({
            dateFerie: item.dateFerie,
            libelle: item.libelle,
            annee: String(item.annee),
            recurrent: item.recurrent,
        });
        setDialogOpen(true);
    };
    const handleSave = async () => {
        if (!apiToken || !form.dateFerie || !form.libelle)
            return;
        setSaving(true);
        try {
            const fields = {
                DateFerie: form.dateFerie,
                Libelle: form.libelle,
                Annee: parseInt(form.annee, 10) || new Date().getFullYear(),
                Recurrent: form.recurrent,
            };
            if (editingItem) {
                await updateJourFerie(apiToken, editingItem.id, fields);
                setItems((prev) => prev.map((j) => j.id === editingItem.id ? { ...j, dateFerie: form.dateFerie, libelle: form.libelle, annee: fields.Annee, recurrent: form.recurrent } : j));
                showMessage("Jour ferié modifié", "success");
            }
            else {
                const id = await createJourFerie(apiToken, fields);
                setItems((prev) => [...prev, { id, dateFerie: form.dateFerie, libelle: form.libelle, annee: fields.Annee, recurrent: form.recurrent }].sort((a, b) => (a.dateFerie > b.dateFerie ? 1 : -1)));
                showMessage("Jour ferié ajouté", "success");
            }
            setDialogOpen(false);
        }
        catch {
            showMessage("Erreur sauvegarde", "error");
        }
        finally {
            setSaving(false);
        }
    };
    const handleDelete = useCallback(async (id) => {
        if (!apiToken || !confirm("Supprimer ce jour férié ?"))
            return;
        try {
            await deleteJourFerie(apiToken, id);
            setItems((prev) => prev.filter((j) => j.id !== id));
            showMessage("Jour férié supprimé", "success");
        }
        catch {
            showMessage("Erreur suppression", "error");
        }
    }, [apiToken]);
    const formatDate = (iso) => {
        if (!iso)
            return "";
        const [y, m, d] = iso.split("-");
        return `${d}/${m}/${y}`;
    };
    if (loading)
        return _jsx(Spinner, { label: "Chargement..." });
    return (_jsxs("div", { children: [_jsxs("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }, children: [_jsx("h1", { children: "Jours f\u00E9ri\u00E9s" }), _jsxs("div", { style: { display: "flex", gap: "8px" }, children: [_jsx(Button, { icon: _jsx(AddRegular, {}), appearance: "primary", onClick: openCreate, children: "Ajouter" }), _jsx(Button, { icon: _jsx(ArrowSyncRegular, {}), onClick: fetchData, appearance: "subtle", children: "Actualiser" })] })] }), error && _jsx(MessageBar, { intent: "error", style: { marginBottom: "16px" }, children: _jsx(MessageBarBody, { children: error }) }), success && _jsx(MessageBar, { intent: "success", style: { marginBottom: "16px" }, children: _jsx(MessageBarBody, { children: success }) }), _jsxs(Table, { "aria-label": "Jours f\u00E9ri\u00E9s", children: [_jsx(TableHeader, { children: _jsxs(TableRow, { children: [_jsx(TableHeaderCell, { children: "Date" }), _jsx(TableHeaderCell, { children: "Libell\u00E9" }), _jsx(TableHeaderCell, { children: "Ann\u00E9e" }), _jsx(TableHeaderCell, { children: "R\u00E9current" }), _jsx(TableHeaderCell, { children: "Actions" })] }) }), _jsxs(TableBody, { children: [items.map((j) => (_jsxs(TableRow, { children: [_jsx(TableCell, { children: _jsx(Text, { weight: "semibold", children: formatDate(j.dateFerie) }) }), _jsx(TableCell, { children: j.libelle }), _jsx(TableCell, { children: j.annee }), _jsx(TableCell, { children: _jsx(Badge, { color: j.recurrent ? "success" : "informative", children: j.recurrent ? "Oui" : "Non" }) }), _jsx(TableCell, { children: _jsxs("div", { style: { display: "flex", gap: "4px" }, children: [_jsx(Button, { icon: _jsx(EditRegular, {}), appearance: "subtle", size: "small", onClick: () => openEdit(j) }), _jsx(Button, { icon: _jsx(DeleteRegular, {}), appearance: "subtle", size: "small", onClick: () => handleDelete(j.id), style: { color: "#ef4444" } })] }) })] }, j.id))), items.length === 0 && (_jsx(TableRow, { children: _jsx(TableCell, { colSpan: 5, style: { textAlign: "center", padding: "24px" }, children: "Aucun jour f\u00E9ri\u00E9." }) }))] })] }), _jsx(Dialog, { open: dialogOpen, onOpenChange: (_, data) => setDialogOpen(data.open), children: _jsx(DialogSurface, { children: _jsxs(DialogBody, { children: [_jsx(DialogTitle, { action: _jsx(Button, { appearance: "subtle", icon: _jsx(DismissRegular, {}), onClick: () => setDialogOpen(false) }), children: editingItem ? "Modifier le jour férié" : "Ajouter un jour férié" }), _jsxs(DialogContent, { style: { display: "flex", flexDirection: "column", gap: "16px", paddingTop: "16px" }, children: [_jsx(Field, { label: "Date", required: true, children: _jsx(Input, { type: "date", value: form.dateFerie, onChange: (_, d) => {
                                                const year = d.value ? d.value.substring(0, 4) : form.annee;
                                                setForm({ ...form, dateFerie: d.value, annee: year });
                                            } }) }), _jsx(Field, { label: "Libell\u00E9", required: true, children: _jsx(Input, { value: form.libelle, onChange: (_, d) => setForm({ ...form, libelle: d.value }), placeholder: "Ex: Jour de l'an" }) }), _jsx(Field, { label: "Ann\u00E9e", children: _jsx(Input, { type: "number", value: form.annee, onChange: (_, d) => setForm({ ...form, annee: d.value }) }) }), _jsx(Field, { label: "R\u00E9current (chaque ann\u00E9e)", children: _jsx(Switch, { checked: form.recurrent, onChange: (_, d) => setForm({ ...form, recurrent: d.checked }) }) })] }), _jsxs(DialogActions, { children: [_jsx(Button, { appearance: "secondary", onClick: () => setDialogOpen(false), children: "Annuler" }), _jsx(Button, { appearance: "primary", onClick: handleSave, disabled: saving || !form.dateFerie || !form.libelle, children: saving ? "Enregistrement..." : editingItem ? "Modifier" : "Ajouter" })] })] }) }) })] }));
};
